// This is the file that makes the server blind. No matter what happens
// server-side — breach, subpoena, rogue admin — it can't read your messages
// because it never has the keys. The whole point of this project lives here.
//
// The trick is a hybrid scheme: we generate a fresh key pair and a fresh
// symmetric key per message, then wrap that symmetric key once for each
// recipient using ECDH + HKDF so only their private key can unwrap it.
// The server stores the envelope but can't open any of the wrapped slots.
//
// Concretely, encrypting a message works like this:
//
//   1. Generate a throwaway P-256 keypair `EK_msg`. The private half never
//      leaves this function — only the public half goes in the envelope.
//   2. Generate a random 32-byte symmetric key `MK_msg`. This is the AES-256
//      key that actually locks the plaintext.
//   3. For each recipient, do ECDH between `EK_msg.private` and their
//      identity public key, then run that secret through HKDF to get a
//      per-recipient wrap key, then AES-GCM-encrypt `MK_msg` with it.
//      Each recipient gets their own locked copy of `MK_msg`.
//   4. AES-GCM-encrypt the plaintext under `MK_msg`, with `(channelId,
//      messageId)` bound into the AAD so the ciphertext can't be
//      transplanted into a different message slot.
//   5. Pack everything into the envelope and return it.
//
// Every source of freshness — the ephemeral key, `MK_msg`, every IV — comes
// from `crypto.getRandomValues`. Nothing from caller input gets mixed in.
// The envelope never contains `MK_msg`, the ephemeral private key, or the
// sender's identity private key in any form.

import {
  buildE2eAad,
  type E2eEnvelope,
  type E2eEnvelopeRecipient,
  type E2eRecipient,
  E2eError,
} from "./envelope";
import {
  CONTENT_IV_BYTES,
  E2E_ALG,
  E2E_VERSION,
  GCM_TAG_BYTES,
  MK_BYTES,
  SEC1_UNCOMPRESSED_LENGTH,
  WRAP_IV_BYTES,
  assertSec1UncompressedP256,
  deriveWrapKey,
  getSubtle,
  wipe,
} from "./internals";

// ---------------------------------------------------------------------------
// Public input type
// ---------------------------------------------------------------------------

/** Inputs to {@link encryptE2e}. */
export interface EncryptE2eInput {
  /** The bytes the recipients will recover. */
  plaintext: Uint8Array;
  /**
   * Non-empty list of recipients. Order is preserved into the
   * envelope; `decryptE2e` matches by `accountIdHash` so order does
   * not affect correctness, only deterministic byte output.
   *
   * The same `accountIdHash` MUST NOT appear twice — a duplicate is
   * a programmer bug, and `encryptE2e` rejects it rather than
   * silently producing two entries that would each decrypt to the
   * same `MK_msg`.
   */
  recipients: E2eRecipient[];
  /** Channel ID — bound into the AAD. */
  channelId: string;
  /** Message ID — bound into the AAD. */
  messageId: string;
}

// ---------------------------------------------------------------------------
// Public function
// ---------------------------------------------------------------------------

/**
 * Seal `plaintext` for every recipient in `recipients`. Returns the decoded
 * envelope — pass it to {@link serializeE2eEnvelope} to get the bytes the
 * server stores (and cannot read).
 *
 * What this guarantees:
 *
 *   * Every listed recipient, given only their identity private key and the
 *     envelope, gets back `plaintext` byte-for-byte.
 *   * Anyone not in the list — including the server — is stuck. `MK_msg` is
 *     wrapped only for the listed recipients, and the content ciphertext is
 *     bound to `(channelId, messageId)` via AAD.
 *   * The envelope contains no copy of `plaintext`, no private key material,
 *     and no copy of `MK_msg` — they're either wiped before return or never
 *     written to the envelope in the first place.
 *
 * @throws on empty `recipients`, duplicate `accountIdHash`, malformed
 *   recipient public keys, or Web Crypto failures.
 */
export async function encryptE2e(input: EncryptE2eInput): Promise<E2eEnvelope> {
  if (input.recipients.length === 0) {
    throw new E2eError(
      "empty_recipients",
      "encryptE2e requires at least one recipient",
    );
  }

  // Catch duplicate recipients up front — we compare on `accountIdHash`
  // because that's the field the envelope uses as an index. Linear scan is
  // fine; a single message has at most a few dozen recipients in practice.
  for (let i = 0; i < input.recipients.length; i++) {
    const a = input.recipients[i]!;
    if (a.accountIdHash.length !== 32) {
      throw new E2eError(
        "bad_field_length",
        `recipients[${i}].accountIdHash must be 32 bytes, got ${a.accountIdHash.length}`,
      );
    }
    assertSec1UncompressedP256(
      `recipients[${i}].identityPublicKey`,
      a.identityPublicKey,
    );
    for (let j = i + 1; j < input.recipients.length; j++) {
      const b = input.recipients[j]!;
      if (uint8Equal(a.accountIdHash, b.accountIdHash)) {
        throw new E2eError(
          "bad_shape",
          `duplicate accountIdHash at recipients[${i}] and recipients[${j}]`,
        );
      }
    }
  }

  const subtle = getSubtle();

  // --- Step 1: generate the per-message ephemeral keypair `EK_msg`. ---
  // We mark the private key non-extractable — we only ever need the public
  // half in the envelope, and the private half should stay locked in Web
  // Crypto's internal state until GC takes it.
  const ephemeralKeyPair = (await subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    false,
    ["deriveBits"],
  )) as CryptoKeyPair;

  const senderEphPub = new Uint8Array(
    await subtle.exportKey("raw", ephemeralKeyPair.publicKey),
  );
  if (senderEphPub.length !== SEC1_UNCOMPRESSED_LENGTH) {
    throw new Error(
      `encryptE2e: ephemeral public key was ${senderEphPub.length} bytes, expected ${SEC1_UNCOMPRESSED_LENGTH}`,
    );
  }

  // --- Step 2: generate the per-message symmetric key `MK_msg`. ---
  // 32 fresh CSPRNG bytes. We import them into Web Crypto for the content
  // encryption step, and keep the raw bytes around so we can wrap them for
  // each recipient. The raw buffer is wiped before this function returns.
  const mkRaw = new Uint8Array(MK_BYTES);
  crypto.getRandomValues(mkRaw);
  const mkKey = await subtle.importKey(
    "raw",
    mkRaw, // Web Crypto copies this into its own state
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"],
  );

  // --- Step 3: per-recipient ECDH + HKDF + wrap of MK_msg. ---
  const recipients: E2eEnvelopeRecipient[] = new Array(input.recipients.length);
  try {
    for (let i = 0; i < input.recipients.length; i++) {
      const r = input.recipients[i]!;

      // Import the recipient's public key for ECDH. Web Crypto rejects
      // off-curve points here, which is what we want — better to surface
      // a real error than silently produce garbage ciphertext.
      let recipientPub: CryptoKey;
      try {
        recipientPub = await subtle.importKey(
          "raw",
          r.identityPublicKey.slice(),
          { name: "ECDH", namedCurve: "P-256" },
          false,
          [],
        );
      } catch (cause) {
        throw new E2eError(
          "bad_shape",
          `recipients[${i}].identityPublicKey could not be imported as a P-256 point`,
          cause,
        );
      }

      const sharedSecret = new Uint8Array(
        await subtle.deriveBits(
          { name: "ECDH", public: recipientPub },
          ephemeralKeyPair.privateKey,
          256,
        ),
      );
      let wrapKey: CryptoKey;
      try {
        wrapKey = await deriveWrapKey(
          sharedSecret,
          senderEphPub,
          r.accountIdHash,
        );
      } finally {
        wipe(sharedSecret);
      }

      const wrapIv = new Uint8Array(WRAP_IV_BYTES);
      crypto.getRandomValues(wrapIv);

      // No AAD on the wrap step — the wrap key is already domain-separated
      // by the HKDF info string, and each recipient slot is independent.
      // The content encryption below is what binds things to (channelId, messageId).
      const wrappedKeyBytes = new Uint8Array(
        await subtle.encrypt({ name: "AES-GCM", iv: wrapIv }, wrapKey, mkRaw),
      );
      if (wrappedKeyBytes.length !== MK_BYTES + GCM_TAG_BYTES) {
        throw new Error(
          `encryptE2e: wrapped key length was ${wrappedKeyBytes.length}, expected ${MK_BYTES + GCM_TAG_BYTES}`,
        );
      }

      recipients[i] = {
        // Copy the input account-id-hash so the envelope owns its bytes.
        accountIdHash: r.accountIdHash.slice(),
        wrappedKey: wrappedKeyBytes,
        iv: wrapIv,
      };
    }
  } finally {
    // Wipe the raw MK_msg bytes once every recipient has their wrapped copy.
    // Web Crypto already has its own copy in `mkKey` — this local buffer is
    // done and shouldn't hang around.
    wipe(mkRaw);
  }

  // --- Step 4: encrypt the plaintext under MK_msg. ---
  const contentIv = new Uint8Array(CONTENT_IV_BYTES);
  crypto.getRandomValues(contentIv);
  const aad = buildE2eAad(input.channelId, input.messageId);

  // We pass `aad` into AES-GCM's additionalData slot — this is the part that
  // binds the ciphertext to a specific (channelId, messageId) pair so you
  // can't copy a ciphertext from one message slot into another.
  const ct = new Uint8Array(
    await subtle.encrypt(
      { name: "AES-GCM", iv: contentIv, additionalData: aad },
      mkKey,
      // Pass a slice so we're explicit that we don't want the caller's
      // plaintext buffer aliased into Web Crypto's state.
      input.plaintext.slice(),
    ),
  );

  return {
    v: E2E_VERSION,
    alg: E2E_ALG,
    senderEphPub,
    recipients,
    ct,
    iv: contentIv,
    aad,
  };
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function uint8Equal(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
